/**
 * "Algo aconteceu aqui": um pulso curto no avatar ou na peça que mudou —
 * outro gestor iniciou/pausou/finalizou, mudou o posto, ou o PPCP digitou a
 * data e o ambiente andou de setor. Sem som, sem toast: só o olho sendo
 * puxado para o lugar certo.
 *
 * Chaves: "amb:<chave do ambiente>" e "pes:<id do colaborador>". O valor é
 * um número de sequência (o elemento remonta o anel e anima de novo); some
 * sozinho depois de DURACAO_PULSO_MS.
 */
import { useEffect, useRef, useState } from "react";

import { movimentosEntreSetores } from "./dados/combinar";

export const DURACAO_PULSO_MS = 1600;
const SEM_PULSOS = new Map();

/** Chaves de pulso das mudanças do estado do app (useEstadoMapa). */
export function chavesDasMudancas(mudancas) {
  const chaves = new Set();
  (mudancas || []).forEach((m) => {
    if (m.alvo === "ambiente") {
      chaves.add(`amb:${m.id}`);
      return;
    }
    const depois = m.depois || {};
    const antes = m.antes || {};
    if (depois.colaborador_id) chaves.add(`pes:${depois.colaborador_id}`);
    if (antes.colaborador_id && antes.colaborador_id !== depois.colaborador_id) chaves.add(`pes:${antes.colaborador_id}`);
    if (depois.ambiente_chave) chaves.add(`amb:${depois.ambiente_chave}`);
  });
  return [...chaves];
}

/** Chaves dos ambientes que chegaram a um lugar novo pela planilha. */
export function chavesDosMovimentos(movimentos) {
  return (movimentos || []).filter((m) => m.para && m.chave).map((m) => `amb:${m.chave}`);
}

/**
 * Ambientes que andaram entre duas cargas da planilha (só quando os
 * `dados` mudam; o estado do app mexendo nas combinadas não conta).
 */
export function useMovimentosDaPlanilha(dados, combinadas) {
  const anteriorRef = useRef({ dados: null, combinadas: null });
  const [movimentos, setMovimentos] = useState([]);
  useEffect(() => {
    const anterior = anteriorRef.current;
    if (anterior.dados && anterior.dados !== dados) {
      setMovimentos(movimentosEntreSetores(anterior.combinadas, combinadas));
    }
    anteriorRef.current = { dados, combinadas };
  }, [dados, combinadas]);
  return movimentos;
}

export default function usePulsos(mudancas, movimentos) {
  const [pulsos, setPulsos] = useState(SEM_PULSOS);
  const seqRef = useRef(0);
  const timersRef = useRef(new Set());
  // Cada lista pulsa UMA vez (pela identidade): sem isto, o próprio render
  // do pulso reprocessaria as mesmas mudanças e o pulso nunca acabaria.
  const vistosRef = useRef({ mudancas: null, movimentos: null });

  useEffect(() => {
    const vistos = vistosRef.current;
    const novasMudancas = mudancas !== vistos.mudancas ? mudancas : null;
    const novosMovimentos = movimentos !== vistos.movimentos ? movimentos : null;
    vistosRef.current = { mudancas, movimentos };
    const chaves = [...chavesDasMudancas(novasMudancas), ...chavesDosMovimentos(novosMovimentos)];
    if (!chaves.length) return;
    seqRef.current += 1;
    const seq = seqRef.current;
    setPulsos((atual) => {
      const novo = new Map(atual);
      chaves.forEach((chave) => novo.set(chave, seq));
      return novo;
    });
    const timer = setTimeout(() => {
      timersRef.current.delete(timer);
      setPulsos((atual) => {
        const restantes = [...atual.entries()].filter(([, valor]) => valor !== seq);
        return restantes.length === atual.size ? atual : new Map(restantes);
      });
    }, DURACAO_PULSO_MS);
    timersRef.current.add(timer);
  }, [mudancas, movimentos]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => timers.forEach(clearTimeout);
  }, []);

  return pulsos;
}
